/**
 * ResmoHelper — proactive in-app support assistant.
 *
 * - Floating mascot bubble (bottom-left, above the Feedback button) on every
 *   authed page. Opens a chat panel that answers questions about any Resmo
 *   feature (Claude via /api/assistant/chat, multi-turn per session).
 * - PROACTIVE: listens for `resmo:client-error` events (fired by the axios
 *   interceptor on 5xx/network failures) and pops open with an offer to help.
 *   5-minute cooldown between proactive pops; escalating copy on repeat errors.
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { LifeBuoy, Send, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import ProgressMeter from "./ProgressMeter";

const GREETING = {
  role: "assistant",
  content: "Hey! I'm the Resmo Helper 🐾 — ask me anything about the app: tailoring, the Studio, mock interviews, credits… or tell me what's not working and I'll help you sort it.",
};

export default function ResmoHelper() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return sessionStorage.getItem("resmo:helper:session") || ""; } catch { return ""; }
  });
  const [pulse, setPulse] = useState(false);
  const lastError = useRef(null);
  const errorCount = useRef(0);
  const lastProactive = useRef(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  // Proactive: pop open when the tester hits real errors.
  useEffect(() => {
    const onError = (e) => {
      if (!user?.id) return;
      errorCount.current += 1;
      lastError.current = e.detail || null;
      const now = Date.now();
      if (open || now - lastProactive.current < 5 * 60 * 1000) {
        setPulse(true);
        return;
      }
      lastProactive.current = now;
      const msg = errorCount.current >= 2
        ? "Still hitting a wall? 😟 Tell me what you were trying to do — I'll walk you through it, and the error's already been flagged to the team."
        : "👋 I noticed that last action didn't go through. Since this is a sandbox beta, the servers may just be warming up — give it ~30 seconds and retry. Want a hand with anything in the meantime?";
      setMessages((m) => [...m, { role: "assistant", content: msg }]);
      setOpen(true);
    };
    window.addEventListener("resmo:client-error", onError);
    return () => window.removeEventListener("resmo:client-error", onError);
  }, [user?.id, open]);

  if (!user?.id) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        message: text,
        session_id: sessionId || null,
        page: location.pathname,
        error_context: lastError.current
          ? `${lastError.current.url || ""} → ${lastError.current.status ?? "network"}: ${lastError.current.message || ""}`.slice(0, 400)
          : null,
      });
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        try { sessionStorage.setItem("resmo:helper:session", data.session_id); } catch { /* ignore */ }
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      lastError.current = null;
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: e.response?.data?.detail || "Hmm, I couldn't reach my brain just now — give it a few seconds and try again.",
      }]);
    }
    setSending(false);
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => { setOpen(true); setPulse(false); }}
          className="fixed bottom-20 left-5 z-[60] flex items-center gap-2 rounded-full px-3.5 py-2.5"
          style={{
            background: "#0F1B2D", color: "#FFC145", border: "2px solid #FFC145",
            boxShadow: pulse ? "0 0 0 4px rgba(255,106,0,0.35), 4px 4px 0 0 rgba(0,0,0,0.3)" : "4px 4px 0 0 rgba(0,0,0,0.3)",
            fontWeight: 700,
          }}
          data-testid="helper-open-btn"
          title="Ask the Resmo Helper anything"
        >
          <LifeBuoy size={16} className={pulse ? "animate-pulse" : ""} />
          <span className="font-mono-rm text-[10px] uppercase tracking-widest">Help</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-5 left-5 z-[95] flex flex-col rounded-lg overflow-hidden"
          style={{
            width: 360, maxWidth: "calc(100vw - 40px)", height: 480, maxHeight: "70vh",
            background: "#FFFDF7", border: "3px solid #0F1B2D", boxShadow: "8px 8px 0 0 #0F1B2D",
          }}
          data-testid="helper-panel"
        >
          <div className="flex items-center gap-2 px-3.5 py-2.5 flex-shrink-0"
               style={{ background: "#0F1B2D" }}>
            <LifeBuoy size={15} style={{ color: "#FFC145" }} />
            <div>
              <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#FFC145", fontWeight: 700 }}>
                Resmo Helper
              </div>
              <div className="font-mono-rm text-[8px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
                Beta support · answers in seconds
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="ml-auto p-1 rounded hover:opacity-70"
                    style={{ color: "#94A3B8" }} aria-label="Close" data-testid="helper-close-btn">
              <X size={15} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5" data-testid="helper-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className="rounded-lg px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-wrap"
                  style={m.role === "user"
                    ? { background: "#0F1B2D", color: "#F8FAFC", maxWidth: "85%" }
                    : { background: "#FFF2EC", color: "#0F1B2D", border: "1.5px solid #FDBA74", maxWidth: "88%" }}
                  data-testid={`helper-msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="max-w-[85%]">
                <ProgressMeter compact expectedSeconds={8} label="Thinking" testid="helper-thinking" />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 p-2.5 flex-shrink-0 border-t-2" style={{ borderColor: "#0F1B2D", background: "white" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask me anything about Resmo…"
              className="flex-1 text-[13px] px-2.5 py-2 rounded outline-none"
              style={{ border: "2px solid #CBD5E1", background: "#FFFDF7" }}
              data-testid="helper-input"
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className="p-2.5 rounded disabled:opacity-40"
              style={{ background: "#FF6A00", color: "white", border: "2px solid #0F1B2D" }}
              data-testid="helper-send-btn"
              aria-label="Send"
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
